<template>
  <MainLayout>
    <BackButton text="back to projects list" :route="$routes.projects()" />
    <v-container max-width="1280">
      <h2 class="text-center mb-12">Project Comparison   </h2>
      <v-row>
        <v-col>
          <v-alert icon="fas fa-info-circle" closable color="grey" class="ml-5 mr-5 mb-5">
              The comparison that takes place in this page, is taking in mind ONLY
              the common <b>Aggregation Levels</b> ({{
                commonAggregations.join(", ")
              }}), <b>Dimensions</b> ({{ commonDimensions.join(", ") }}) and
              <b>Sectors</b> ({{ commonSectors.join(", ") }}) of the selected
              projects
          </v-alert>
          <v-tabs v-model="tab" bg-color="primary">
            <v-tab style="text-transform: none" value="one">KPIs</v-tab>
            <v-tab style="text-transform: none" value="two">PSIs</v-tab>
          </v-tabs>
          <v-window v-model="tab">
            <v-list-group class="mb-2" v-if="errors">
              <v-list-item variant="flat" class="danger">
                {{ errors }}
              </v-list-item>
            </v-list-group>
            <v-window-item value="one"
                           reverse-transition="fade-transition"
                           transition="false"
            >
              <CollapsibleHeader title="SII and SPI per aggregation level" class="my-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <v-row>
                      <v-col cols="6">
                        <SimpleVerticalBarChart
                            class="whole-chart-border"
                            v-if="siiChartData"
                            :chartData="siiChartData"
                            :axisLabels="{ x: 'Aggregation Level', y: 'Score' }"
                            :showColorCode="false"
                            :aspectRatio="{ width: 2, height: 1 }"
                            title="Final SII Scores"
                            id="graph1"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </SimpleVerticalBarChart>
                      </v-col>

                      <v-col cols="6">
                        <SimpleVerticalBarChart
                            class="whole-chart-border"
                            v-if="spiChartData"
                            :chartData="spiChartData"
                            :axisLabels="{ x: 'Aggregation Level', y: 'Score' }"
                            :showColorCode="false"
                            :aspectRatio="{ width: 2, height: 1 }"
                            title="Final SPI Scores"
                            id="graph2"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </SimpleVerticalBarChart>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </CollapsibleHeader>

              <CollapsibleHeader title="Dimension and sector scores per aggregation level" class="mb-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <v-row>
                      <v-col cols="6">
                        <RadarChartWithSelect
                            class="whole-chart-border"
                            title="Dimensions score per Aggregation Level"
                            selectPrompt="Aggregation Level:"
                            :itemsData="radarChartData"
                            id="graph3"
                        />
                      </v-col>

                      <v-col cols="6">
                        <RadarChartWithSelect
                            class="whole-chart-border"
                            title="Sectors score per Aggregation Level"
                            selectPrompt="Aggregation Level:"
                            :itemsData="sectorsRadarChartData"
                            id="graph4"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </CollapsibleHeader>

              <CollapsibleHeader title="Aggregation level score per dimension and sector" class="mb-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <v-row>
                      <v-col cols="6">
                        <ChartWithSelect
                            class="whole-chart-border"
                            :availableItems="availableDimensions"
                            :itemsData="dimensionData"
                            :showFinalSII="false"
                            selectPrompt="Dimension:"
                            id="graph5"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </ChartWithSelect>
                      </v-col>

                      <v-col cols="6">
                        <ChartWithSelect
                            class="whole-chart-border"
                            :availableItems="availableSectors"
                            :itemsData="sectorData"
                            :show-final-SII="false"
                            isSpiOrSii="spi"
                            selectPrompt="Sector:"
                            id="graph6"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </ChartWithSelect>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </CollapsibleHeader>

              <CollapsibleHeader title="Individual KPI scores per aggregation level" class="mb-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <HorizontalBarChartWithSelect
                        comparison
                        :items-data="kpisData"
                        :available-items="Object.keys(kpisData)"
                        chartType="horizontal"
                        selectPrompt="KPI:"
                        :axisLabels="{ x: 'Score', y: '' }"
                        :key="`bar_key_3`"
                        id="bar_compare_hor"
                        annotations=""
                    />
                  </v-card>
                </template>
              </CollapsibleHeader>
            </v-window-item>
            <v-window-item value="two"
                           reverse-transition="false"
                           transition="fade-transition">
              <v-col cols="12 mb-3">
                <v-card class="px-3 py-6" elevation="3">
                  <SimpleVerticalBarChart
                      v-if="callImpactData && callImpactData.labels.length > 0"
                      :chartData="callImpactData"
                      :show-color-code="false"
                      :axisLabels="{ x: 'Call Impact Id', y: 'Call Impact Score' }"
                      title="Call Impact Data"
                      id="psi1"
                  />
                </v-card>
              </v-col>
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
    </v-container>

  </MainLayout>
</template>

<script setup>
import MainLayout from "../../Layouts/Main.vue";
import SimpleVerticalBarChart from "../../Charts/simple/SimpleVerticalBarChart.vue";
import RadarChartWithSelect from "@/Charts/select/RadarChartWithSelect.vue";
import {computed, ref} from "vue";
import StringManipulation from "@/Mixins/StringManipulation";
import ChartWithSelect from "@/Charts/select/ChartWithSelect.vue";
import BackButton from "@/Shared/BackButton.vue";
import * as $routes from "@/packs/routes";
import HorizontalBarChartWithSelect from "@/Charts/select/HorizontalBarChartWithSelect.vue";
import ColorCodePalette from "@/Shared/ColorCodePalette.vue";
import CollapsibleHeader from "@/Shared/CollapsibleHeader.vue";

const props = defineProps({
  projects: {
    type: Object,
    required: true,
  },
  siiChartData: {
    type: Object,
    required: false,
  },
  spiChartData: {
    type: Object,
    required: false,
  },
  dimensionData: {
    type: Object,
    required: false,
  },
  sectorData: {
    type: Object,
    required: false,
  },
  availableDimensions: {
    type: Array,
    required: false,
  },
  availableSectors: {
    type: Array,
    default: null,
    required: false,
  },
  callImpactData: {
    type: Object,
    required: false,
  },
  callImpactGradientColors: {
    type: Array,
    required: false,
  },
  commonAggregations: {
    type: Array,
    required: false,
  },
  commonDimensions: {
    type: Array,
    required: false,
  },
  commonSectors: {
    type: Array,
    required: false,
  },
  errors: {
    type: String,
    required: false,
  },
  kpisData: {
    type: String,
    required: false
  }
});

//Handle the different tabs
const tab = ref(null);

//Pre configure the bg colors for multiple project comparison
const FRONTEND_CHART_COLORS = [
  "rgba(255, 99, 132, 0.65)",
  "rgba(54, 162, 235, 0.65)",
  "rgba(255, 205, 86, 0.65)",
  "rgba(75, 192, 192, 0.65)",
];

const RADAR_CHART_COLORS = {
  // Red Palette: Soft transparent fills, bold distinct lines/borders
  "Building": [
    "rgba(239, 68, 68, 0.2)",  // Soft Red fill
    "rgba(220, 38, 38, 0.4)",  // Medium Red fill
    "rgba(185, 28, 28, 0.6)",  // Darker Red fill
    "rgba(153, 27, 27, 0.8)"   // Deep Red fill
  ],
  // Blue Palette: Excellent contrast against the red
  "PEB": [
    "rgba(59, 130, 246, 0.2)",  // Soft Blue fill
    "rgba(37, 99, 235, 0.4)",   // Medium Blue fill
    "rgba(29, 78, 216, 0.6)",   // Darker Blue fill
    "rgba(30, 64, 175, 0.8)"    // Deep Blue fill
  ],
  // Green Palette: Highly distinguishable from blue and red
  "PED": [
    "rgba(34, 197, 94, 0.2)",   // Soft Green fill
    "rgba(22, 163, 74, 0.4)",   // Medium Green fill
    "rgba(21, 128, 61, 0.6)",   // Darker Green fill
    "rgba(22, 101, 52, 0.8)"    // Deep Green fill
  ],
  // Yellow-Orange Palette: Warm, distinct, and avoids turning muddy when blended
  "City": [
    "rgba(245, 158, 11, 0.2)",  // Soft Amber fill
    "rgba(217, 119, 6, 0.4)",   // Medium Amber fill
    "rgba(180, 83, 9, 0.6)",    // Darker Amber fill
    "rgba(146, 64, 14, 0.8)"    // Deep Orange/Brown fill
  ]
};

const projectColorCoding = computed(() => {
  return props.siiChartData.datasets.map((dataset) => dataset.backgroundColor);
});

//Handle data for radar Charts
const radarChartData = computed(() => {
  let radarDataHash = {};

  // 1. First pass: Collect all unique dimension keys (labels) and aggregation levels
  const allDimensionKeys = Object.keys(props.dimensionData).sort(); // ["Economic", "Energy", ...]

  // Find all unique aggregation labels ("Building", "PEB", etc.) across the data
  const uniqueLabels = new Set();
  for (let dKey in props.dimensionData) {
    props.dimensionData[dKey].data.labels.forEach(l => uniqueLabels.add(l));
  }

  // 2. Initialize the hash structure cleanly for each aggregation level
  uniqueLabels.forEach(label => {
    radarDataHash[label] = {
      labels: allDimensionKeys.map(key => StringManipulation.methods.clearAndCapitalize(key)),
      datasets: []
    };
  });

  // 3. Second pass: Map data strictly by matching the dimension index
  for (let dKey in props.dimensionData) {
    const dimension = props.dimensionData[dKey];
    const labels = dimension.data.labels; // ["Building", "PEB", ...]
    const dimensionIndex = allDimensionKeys.indexOf(dKey); // Find its true position (e.g., 2 for Environmental)

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];

      dimension.data.datasets.forEach((dataset, datasetIdx) => {
        // Initialize dataset if it doesn't exist yet
        if (!radarDataHash[label].datasets[datasetIdx]) {
          radarDataHash[label].datasets[datasetIdx] = {
            label: dataset.label,
            // Pre-fill the array with nulls matching the total number of dimensions
            data: new Array(allDimensionKeys.length).fill(null),
            fill: true,
            spanGaps: true,
            backgroundColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "rgba(0,0,0,0.1)",
            borderColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "rgba(0,0,0,0.4)",
            pointBackgroundColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "#000",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "#000",
          };
        }

        // Place the value exactly at its correct label slot index
        radarDataHash[label].datasets[datasetIdx].data[dimensionIndex] = dataset.data[i];
      });
    }
  }

  return radarDataHash;
});

const sectorsRadarChartData = computed(() => {
  let radarDataHash = {};
  const allSectorKeys = Object.keys(props.sectorData).sort();

  const uniqueLabels = new Set();
  for (let sKey in props.sectorData) {
    props.sectorData[sKey].data.labels.forEach(l => uniqueLabels.add(l));
  }

  uniqueLabels.forEach(label => {
    radarDataHash[label] = {
      labels: allSectorKeys.map(key => StringManipulation.methods.clearAndCapitalize(key)),
      datasets: []
    };
  });

  for (let sKey in props.sectorData) {
    const sector = props.sectorData[sKey];
    const labels = sector.data.labels;
    const sectorIndex = allSectorKeys.indexOf(sKey);

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];

      sector.data.datasets.forEach((dataset, datasetIdx) => {
        if (!radarDataHash[label].datasets[datasetIdx]) {
          radarDataHash[label].datasets[datasetIdx] = {
            label: dataset.label,
            data: new Array(allSectorKeys.length).fill(null),
            fill: true, // Switched to true to fix the inconsistency from your original code
            backgroundColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "rgba(0,0,0,0.1)",
            borderColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "rgba(0,0,0,0.4)",
            pointBackgroundColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "#000",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: RADAR_CHART_COLORS[label]?.[datasetIdx] || "#000",
          };
        }

        radarDataHash[label].datasets[datasetIdx].data[sectorIndex] = dataset.data[i];
      });
    }
  }
  return radarDataHash;
});
</script>

<style scoped>

.whole-chart-border {
  border: solid 1px #999999;
  padding: 15px;
}

</style>
